import { TERRAIN_OVERLAYS, TERRAIN_TAGLINES, TRADE_TAGLINES } from "../config/heroImages";
import COLORS from "../config/colors";

const C = COLORS;

function ShieldCheck() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
      <path d="M12 2L4 6v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V6l-8-4z" opacity="0.3"/>
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 13l-3-3 1.41-1.41L10 11.17l5.59-5.59L17 7l-7 7z"/>
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

const TRUST_BADGES = [
  "Permits Pulled",
  "Locally Operated",
  "Fixed-Bid Contracts",
];

export default function LandingHero({ market, trade, onCTAClick }) {
  const terrain    = market?.terrain || "midwest";
  const tradeSlug  = trade?.slug     || "decks";
  const overlay    = TERRAIN_OVERLAYS[terrain]    || TERRAIN_OVERLAYS["midwest"];
  const terrainTag = TERRAIN_TAGLINES[terrain]    || TERRAIN_TAGLINES["midwest"];
  const tradeTag   = TRADE_TAGLINES[tradeSlug]    || TRADE_TAGLINES["decks"];

  return (
    <div
      role="banner"
      style={{
        position: "relative",
        width: "100%",
        minHeight: 580,
        height: "88vh",
        maxHeight: 920,
        overflow: "hidden",
        fontFamily: "'DM Sans', sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Terrain-tinted overlay (over the fixed TradeBackground) ── */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: overlay }} />

      {/* ── Bottom vignette ── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "30%",
          background: "linear-gradient(to top, rgba(0,0,0,0.35), transparent)",
        }}
      />

      {/* ── Content ── */}
      <div
        className="hero-content"
        style={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "100px 56px 80px",
          maxWidth: 960,
        }}
      >
      <style>{`
        @media (max-width: 640px) {
          .hero-content { padding: 80px 24px 60px !important; }
        }
      `}</style>
        {/* Trust badges */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 32 }}>
          {TRUST_BADGES.map(badge => (
            <span
              key={badge}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 14px",
                borderRadius: 24,
                background: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.22)",
                color: C.white,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.3,
                lineHeight: 1,
              }}
            >
              <ShieldCheck />
              {badge}
            </span>
          ))}
        </div>

        {/* Market + trade eyebrow */}
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 3.5,
            color: C.honey,
            marginBottom: 14,
          }}
        >
          {market?.displayName || "Your Area"}
          &nbsp;·&nbsp;
          {trade?.displayName || "Home Improvement"}
        </div>

        {/* Headline */}
        <h1
          style={{
            margin: "0 0 20px",
            fontFamily: "'Playfair Display', serif",
            fontWeight: 800,
            fontSize: "clamp(40px, 5.5vw, 78px)",
            lineHeight: 1.06,
            color: C.white,
            textShadow: "0 2px 32px rgba(0,0,0,0.45)",
          }}
        >
          Premium {trade?.displayName || "Trades"}
          <br />
          <em style={{ color: C.honey, fontStyle: "italic" }}>Done Right.</em>
        </h1>

        {/* Trade tagline */}
        <p
          style={{
            margin: "0 0 12px",
            fontSize: "clamp(15px, 1.7vw, 19px)",
            lineHeight: 1.7,
            color: "rgba(255,255,255,0.86)",
            maxWidth: 580,
          }}
        >
          {tradeTag}
        </p>

        {/* Terrain tagline */}
        <p
          style={{
            margin: "0 0 40px",
            fontSize: 13,
            color: "rgba(255,255,255,0.55)",
            maxWidth: 520,
            fontStyle: "italic",
          }}
        >
          {terrainTag}
        </p>

        {/* CTA row */}
        <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={onCTAClick}
            style={{
              padding: "18px 46px",
              borderRadius: 12,
              border: "none",
              background: C.honey,
              color: C.white,
              fontSize: 17,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              letterSpacing: 0.3,
              boxShadow: "0 6px 32px rgba(196,145,59,0.55)",
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 10px 40px rgba(196,145,59,0.65)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = "";
              e.currentTarget.style.boxShadow = "0 6px 32px rgba(196,145,59,0.55)";
            }}
          >
            Get Free Estimate →
          </button>

          <span
            style={{
              color: "rgba(255,255,255,0.60)",
              fontSize: 13,
            }}
          >
            2 minutes · No commitment
          </span>
        </div>
      </div>

      {/* ── Scroll nudge ── */}
      <button
        onClick={onCTAClick}
        aria-label="Scroll to estimate form"
        style={{
          position: "absolute",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          background: "none",
          border: "none",
          color: "rgba(255,255,255,0.45)",
          cursor: "pointer",
          padding: 8,
          zIndex: 3,
          animation: "heroScroll 2.5s ease-in-out infinite",
        }}
      >
        <ChevronDown />
      </button>

      {/* heroScroll animation defined globally in TradesPlatform */}
    </div>
  );
}
